import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaPause, FaPlay, FaArrowUp } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  globalAudio,
  getUserPaused,
  setUserPaused,
} from "../utils/globalAudio";
import SmoothScroll from "./premium/SmoothScroll";
import PageTransition from "./premium/PageTransition";
import LuxuryCursor from "./premium/LuxuryCursor";
import ScrollProgress from "./premium/ScrollProgress";
import useFloatingDrag from "../hooks/useFloatingDrag";

const WEBSITE_PAGES = [
  "/",
  "/about-us",
  "/benefits",
  "/gallery",
  "/milestones",
  "/blog",
  "/community",
  "/contact",
  "/courses",
  "/live-classes",
  "/trainers",
  "/instructors",
  "/pricing",
  "/privacy-policy",
  "/terms-conditions",
  "/refund-policy",
  "/cookie-policy",
];

const Layout = ({ children }) => {
  const location  = useLocation();
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Mirror audio playing state for the UI button
  const [musicPlaying, setMusicPlaying]   = useState(!globalAudio.paused);

  const isWebsitePage = WEBSITE_PAGES.includes(location.pathname);
  const floatDrag = useFloatingDrag("dy_float_music_pos");

  // ── Scroll-to-top visibility ──────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 350);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScrollToTop = () => {
    if (window.lenis?.scrollTo) {
      window.lenis.scrollTo(0, { duration: 1.2 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ── Audio logic on route change ───────────────────────────────────────────
  useEffect(() => {
    if (isWebsitePage) {
      // Only attempt to play if the user has NOT manually paused
      if (!getUserPaused()) {
        const tryPlay = async () => {
          try {
            await globalAudio.play();
            setMusicPlaying(true);
          } catch {
            // Autoplay blocked   wait for first user interaction
            const onInteract = async () => {
              if (!getUserPaused()) {
                try {
                  await globalAudio.play();
                  setMusicPlaying(true);
                } catch {}
              }
              window.removeEventListener("click", onInteract);
            };
            window.addEventListener("click", onInteract);
          }
        };
        if (globalAudio.paused) {
          tryPlay();
        }
      }
      setMusicPlaying(!globalAudio.paused);

    } else {
      if (!globalAudio.paused) {
        globalAudio.pause();
        setMusicPlaying(false);
      }
    }

    // Cleanup: Instantly stop audio when navigating away from website pages
    // (e.g. entering /login, /register, user dashboard, or admin dashboard)
    return () => {
      if (!globalAudio.paused) {
        globalAudio.pause();
        setMusicPlaying(false);
      }
    };
  }, [location.pathname, isWebsitePage]);

  // ── Play / Pause button handler ───────────────────────────────────────────
  const handleMusicToggle = async () => {
    if (globalAudio.paused) {
      try {
        await globalAudio.play();
        setUserPaused(false); // user chose to resume
        setMusicPlaying(true);
      } catch {}
    } else {
      globalAudio.pause();
      setUserPaused(true);   // user explicitly paused
      setMusicPlaying(false);
    }
  };

  return (
    <SmoothScroll>
    <div className="relative overflow-x-hidden">
      <LuxuryCursor />
      <ScrollProgress />
      <div className="premium-grain" />
      <Navbar />
      <main>
        <PageTransition>{children}</PageTransition>
      </main>

      {/* Floating Action Controls on Website Pages (Music + Scroll To Top, positioned cleanly above Chatbot).
          Mobile: sits above the Ask Dythi bubble (which is raised on phones). Draggable on all devices. */}
      {isWebsitePage && (
        <>
        <div ref={floatDrag.constraintsRef} className="fixed inset-2 pointer-events-none" aria-hidden="true" />
        <motion.div
          ref={floatDrag.elRef}
          {...floatDrag.dragProps}
          style={{ x: floatDrag.x, y: floatDrag.y, touchAction: "none" }}
          onPointerDown={floatDrag.startDrag}
          title="Drag to move"
          className="fixed bottom-[172px] right-[18px] md:bottom-[138px] md:right-[27px] z-[94] flex flex-col items-center gap-3 select-none">
          {/* Scroll-to-top button (stacked above music player) */}
          <AnimatePresence>
            {showScrollTop && (
              <motion.button
                initial={{ opacity: 0, scale: 0.7, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.7, y: 15 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -3, scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={floatDrag.guardClick(handleScrollToTop)}
                title="Scroll to Top"
                aria-label="Scroll to Top"
                className="
                  w-12 h-12 rounded-full
                  bg-gradient-to-br from-[#F29A0C] via-[#FF8400] to-[#E08200]
                  text-white
                  flex items-center justify-center
                  shadow-[0_12px_35px_rgba(242,154,12,0.38)]
                  border border-white/40 backdrop-blur-2xl
                  hover:shadow-[0_16px_45px_rgba(242,154,12,0.5)]
                  transition-all duration-300
                  cursor-pointer
                  group
                "
              >
                <FaArrowUp className="text-[13px] transition-transform duration-300 group-hover:-translate-y-0.5" />
              </motion.button>
            )}
          </AnimatePresence>

          {/* Music toggle button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ y: -3, scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            onClick={floatDrag.guardClick(handleMusicToggle)}
            title={musicPlaying ? "Pause Background Music" : "Play Background Music"}
            aria-label={musicPlaying ? "Pause Background Music" : "Play Background Music"}
            className="
              w-12 h-12 rounded-full
              bg-white/95 backdrop-blur-3xl
              border border-[#F4D9B5]
              text-[#F29A0C]
              flex items-center justify-center
              shadow-[0_12px_35px_rgba(242,154,12,0.20)]
              hover:border-[#F29A0C] hover:shadow-[0_16px_45px_rgba(242,154,12,0.32)]
              transition-all duration-300
              cursor-pointer
            "
          >
            {musicPlaying
              ? <FaPause className="text-[12px]" />
              : <FaPlay  className="text-[12px] ml-[2px]" />
            }
          </motion.button>
        </motion.div>
        </>
      )}

      <Footer />
    </div>
    </SmoothScroll>
  );
};

export default Layout;